import React, { useState, useMemo, useEffect } from 'react';
import { Modal, Select, Button, Space, Typography, Spin } from 'antd';
import { SwapOutlined } from '@ant-design/icons';
import { LocationExpense } from '../../../services/ExpenseService';
import DashboardService from '../../../services/DashboardService';
import { formatCurrency } from '../../../utils/formatters';

const { Text } = Typography;

interface CompareLocationsModalProps {
  open: boolean;
  onClose: () => void;
  onCompare: (locationA: string, locationB: string) => void;
  locations: LocationExpense[];
  viewMode?: 'weekly' | 'quarterly';
  startDate?: string;
  endDate?: string;
}

export const CompareLocationsModal: React.FC<CompareLocationsModalProps> = ({
  open,
  onClose,
  onCompare,
  locations,
  viewMode = 'weekly',
  startDate,
  endDate,
}) => {
  const [locationA, setLocationA] = useState<string | null>(null);
  const [locationB, setLocationB] = useState<string | null>(null);
  const [eightWeekAvgSales, setEightWeekAvgSales] = useState<Record<string, number>>({});
  const [eightWeekGfsPercent, setEightWeekGfsPercent] = useState<Record<string, number>>({});
  const [loadingAvgSales, setLoadingAvgSales] = useState(false);

  // Fetch 8-week avg sales and GFS% from dashboard when modal opens (weekly view only)
  useEffect(() => {
    if (open && viewMode === 'weekly' && startDate && endDate) {
      setLoadingAvgSales(true);
      DashboardService.getDashboardData(startDate, endDate, 'sales')
        .then((data) => {
          const avgSalesMap: Record<string, number> = {};
          const gfsPercentMap: Record<string, number> = {};
          data.salesTiers.forEach((tier) => {
            tier.locations.forEach((loc) => {
              // weeklyTotal is the 8-week average weekly sales
              if (loc.weeklyTotal > 0) {
                avgSalesMap[loc.location] = loc.weeklyTotal;
              }
              // gfsPercent is the 8-week GFS%
              if (loc.gfsPercent !== undefined && loc.gfsPercent !== null) {
                gfsPercentMap[loc.location] = loc.gfsPercent;
              }
            });
          });
          setEightWeekAvgSales(avgSalesMap);
          setEightWeekGfsPercent(gfsPercentMap);
        })
        .catch((err) => {
          console.error('Failed to fetch 8-week avg sales:', err);
        })
        .finally(() => {
          setLoadingAvgSales(false);
        });
    }
  }, [open, viewMode, startDate, endDate]);

  // Sort locations by 8-week avg sales if available (weekly), otherwise by current sales
  const locationOptions = useMemo(() => {
    return locations
      .slice()
      .sort((a, b) => {
        if (viewMode === 'weekly' && Object.keys(eightWeekAvgSales).length > 0) {
          const avgA = eightWeekAvgSales[a.location] ?? 0;
          const avgB = eightWeekAvgSales[b.location] ?? 0;
          return avgB - avgA;
        }
        return (b.totalSales ?? 0) - (a.totalSales ?? 0);
      })
      .map((loc) => {
        const avgSales = eightWeekAvgSales[loc.location];
        const gfsPercent = viewMode === 'weekly'
          ? eightWeekGfsPercent[loc.location]
          : loc.expensePercentageOfSales;
        return {
          label: `${loc.location} - ${formatCurrency(avgSales ?? loc.totalSales ?? 0)}`,
          value: loc.location,
          sales: loc.totalSales ?? 0,
          eightWeekAvgSales: avgSales,
          gfsPercent,
          dm: loc.dm,
        };
      });
  }, [locations, eightWeekAvgSales, eightWeekGfsPercent, viewMode]);

  const handleCompare = () => {
    if (locationA && locationB) {
      onCompare(locationA, locationB);
      handleClose();
    }
  };

  const handleClose = () => {
    setLocationA(null);
    setLocationB(null);
    onClose();
  };

  const handleSwap = () => {
    const temp = locationA;
    setLocationA(locationB);
    setLocationB(temp);
  };

  const canCompare = locationA && locationB && locationA !== locationB;

  // Lock body scroll when modal is open (fixes tablet scroll bleed-through)
  useEffect(() => {
    if (open) {
      const originalOverflow = document.body.style.overflow;
      const originalPosition = document.body.style.position;
      const originalWidth = document.body.style.width;
      const scrollY = window.scrollY;

      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.top = `-${scrollY}px`;

      return () => {
        document.body.style.overflow = originalOverflow;
        document.body.style.position = originalPosition;
        document.body.style.width = originalWidth;
        document.body.style.top = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [open]);

  return (
    <Modal
      title="Compare Locations"
      open={open}
      onCancel={handleClose}
      footer={[
        <Button key="clear" onClick={() => { setLocationA(null); setLocationB(null); }}>
          Clear
        </Button>,
        <Button key="cancel" onClick={handleClose}>
          Cancel
        </Button>,
        <Button key="compare" type="primary" onClick={handleCompare} disabled={!canCompare}>
          Compare
        </Button>,
      ]}
      width={500}
      styles={{
        body: {
          overscrollBehavior: 'contain',
          WebkitOverflowScrolling: 'touch',
        },
      }}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Text type="secondary" style={{ fontSize: 12, marginBottom: 8, display: 'block' }}>
          {viewMode === 'weekly'
            ? 'Locations sorted by 8-Wk Avg Weekly Sales (highest first)'
            : 'Locations sorted by quarterly sales (highest first)'}
          {loadingAvgSales && <Spin size="small" style={{ marginLeft: 8 }} />}
        </Text>

        <div>
          <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
            Location A
          </Text>
          <Select
            style={{ width: '100%' }}
            placeholder="Select first location"
            value={locationA}
            onChange={setLocationA}
            showSearch
            allowClear
            filterOption={(input, option) =>
              (option?.value?.toString() ?? '').toLowerCase().includes(input.toLowerCase())
            }
            optionLabelProp="label"
          >
            {locationOptions
              .filter((opt) => opt.value !== locationB)
              .map((opt) => (
                <Select.Option key={opt.value} value={opt.value} label={opt.label}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontWeight: 500 }}>{opt.value}</div>
                      <div style={{ fontSize: 11, color: '#888' }}>DM: {opt.dm}</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      {viewMode === 'weekly' && opt.eightWeekAvgSales !== undefined ? (
                        <>
                          <div style={{ fontWeight: 500, color: '#1890ff' }}>
                            {formatCurrency(opt.eightWeekAvgSales)}
                          </div>
                          <div style={{ fontSize: 10, color: '#888' }}>8-wk avg sales</div>
                        </>
                      ) : (
                        <div style={{ fontWeight: 500, color: '#1890ff' }}>
                          {formatCurrency(opt.sales)}
                        </div>
                      )}
                      {opt.gfsPercent !== undefined && (
                        <div style={{ fontSize: 11, color: '#888' }}>
                          GFS: {opt.gfsPercent.toFixed(2)}%
                        </div>
                      )}
                    </div>
                  </div>
                </Select.Option>
              ))}
          </Select>
        </div>

        <div style={{ textAlign: 'center' }}>
          <Button
            icon={<SwapOutlined rotate={90} />}
            onClick={handleSwap}
            disabled={!locationA && !locationB}
            size="small"
          >
            Swap
          </Button>
        </div>

        <div>
          <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
            Location B
          </Text>
          <Select
            style={{ width: '100%' }}
            placeholder="Select second location"
            value={locationB}
            onChange={setLocationB}
            showSearch
            allowClear
            filterOption={(input, option) =>
              (option?.value?.toString() ?? '').toLowerCase().includes(input.toLowerCase())
            }
            optionLabelProp="label"
          >
            {locationOptions
              .filter((opt) => opt.value !== locationA)
              .map((opt) => (
                <Select.Option key={opt.value} value={opt.value} label={opt.label}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontWeight: 500 }}>{opt.value}</div>
                      <div style={{ fontSize: 11, color: '#888' }}>DM: {opt.dm}</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      {viewMode === 'weekly' && opt.eightWeekAvgSales !== undefined ? (
                        <>
                          <div style={{ fontWeight: 500, color: '#1890ff' }}>
                            {formatCurrency(opt.eightWeekAvgSales)}
                          </div>
                          <div style={{ fontSize: 10, color: '#888' }}>8-wk avg sales</div>
                        </>
                      ) : (
                        <div style={{ fontWeight: 500, color: '#1890ff' }}>
                          {formatCurrency(opt.sales)}
                        </div>
                      )}
                      {opt.gfsPercent !== undefined && (
                        <div style={{ fontSize: 11, color: '#888' }}>
                          GFS: {opt.gfsPercent.toFixed(2)}%
                        </div>
                      )}
                    </div>
                  </div>
                </Select.Option>
              ))}
          </Select>
        </div>

        {locationA && locationB && locationA === locationB && (
          <Text type="danger">Please select two different locations</Text>
        )}
      </Space>
    </Modal>
  );
};
